import { getSessionStorage, parseJson, twClassMerge } from '@healthhub/react-ui';
import { useNavigate } from 'react-router';

import { OutlinedRightArrowIcon } from '@shared/assets-icon';
import type { BreadCrumbDetails } from '@shared/components';
import { Button } from '@shared/components';
import { ROOT_PATH, SHORTCUT_MAIN_PATH } from '@shared/constants/paths';
import { SESSION_KEY } from '@shared/constants/sessionKey';
import useResponsive from '@shared/hooks/useResponsive';

interface PortalBreadcrumbProps {
  breadcrumbs?: BreadCrumbDetails[];
  isIncludedEservices?: boolean;
}

const PortalBreadcrumbs = ({ breadcrumbs = [], isIncludedEservices = true }: PortalBreadcrumbProps) => {
  const navigate = useNavigate();
  const responsive = useResponsive();

  const isFromSingpassShortcut = () => {
    try {
      const { callbackPath = '' } = parseJson<{ callbackPath?: string }>(getSessionStorage(SESSION_KEY.oidcSessionKey));
      return callbackPath.includes(SHORTCUT_MAIN_PATH);
    } catch (error) {
      console.warn('Error parsing OIDC session data', error);
      return false;
    }
  };

  const newBreadcrumbs = isIncludedEservices
    ? [{ path: isFromSingpassShortcut() ? SHORTCUT_MAIN_PATH : ROOT_PATH, title: 'Health e-Services' }, ...breadcrumbs]
    : breadcrumbs;

  const displayBreadcrumbs = newBreadcrumbs?.map(({ customOnClick, path, title }, index) => {
    const onBreadcrumbClick = () => {
      if (customOnClick) {
        customOnClick();
        return;
      }

      if (path) {
        navigate(path);
      }
    };

    const isLast = index + 1 === newBreadcrumbs.length;

    return (
      <div className={twClassMerge('flex items-center gap-0', { 'gap-2': responsive.md })} key={title}>
        {!responsive.md && (
          <OutlinedRightArrowIcon className="size-6 rotate-180 self-start [&_path]:stroke-primary-green-4" />
        )}
        <Button
          autoInsertSpace={false}
          className={twClassMerge('text-primary-green-4 text-sm whitespace-normal wrap-break-word text-left', {
            'font-normal text-primary-green-5 cursor-default': isLast,
          })}
          onClick={!isLast ? onBreadcrumbClick : undefined}
          variant="text"
        >
          {title}
        </Button>
        {!isLast && !!responsive.md && <span className="text-functional-stone">/</span>}
      </div>
    );
  });

  if (!responsive.md) {
    return displayBreadcrumbs[displayBreadcrumbs.length - 2];
  }

  return <div className="flex flex-wrap items-center gap-1">{displayBreadcrumbs}</div>;
};

export default PortalBreadcrumbs;
